import React, { useContext, useMemo } from 'react';
import Bar from './Bar';
import FixedTrendCard from './FixedTrendCard';
import RollingTrendCard from './RollingTrendCard';
import SmartCard from './SmartCard';
import TimeSeries from './TimeSeries';
import TreeMap from './TreeMap';
import Pie from './Pie';
import Donut from './Donut';
import Waterfall from './Waterfall';
import Radar from './Radar';
import PackedBubble from './PackedBubble';
import Scatter from './Scatter';
import Bubble from './Bubble';
import Heatmap from './Heatmap';
import Funnel from './Funnel';
import Sankey from './Sankey';
import Bullet from './Bullet';
import Card from './Card';
import Matrix from './Matrix';
import StackedArea from './StackedArea';
import SimpleGrid from './SimpleGrid';
import StackedBar from './StackedBar';
import ComparisonContext from '../../contexts/ComparisonContext';
import DateInputContext from '../../contexts/DateInputContext';
import relativeDateRangeTypeCheckers from '../../contextProviders/SplashScreenProviders/UserAndAccountProviders/FiscalPeriodsProvider/relativeDateRangeTypeCheckers';
import useIsSameFiscalYear from '../../hooks/useDateScope/buildDateRange/useIsSameFiscalYear';
import IncompatibleComparison from '../IncompatibleComparison';
import useDateScope from '../../hooks/useDateScope';

const V5Gadget = ({
  chartDefinition,
  chartRef,
  useSmallNoDataMessage,
}: {
  chartDefinition: V5ChartDefinition;
  chartRef: React.MutableRefObject<any>;
  useSmallNoDataMessage: boolean;
}) => {
  switch (chartDefinition.gadgetType) {
    case 'matrix':
      return <Matrix chartDefinition={chartDefinition} />;
    case 'bar':
      return (
        <Bar
          chartDefinition={chartDefinition}
          chartRef={chartRef}
          useSmallNoDataMessage={useSmallNoDataMessage}
        />
      );
    case 'stackedBar':
      return (
        <StackedBar chartDefinition={chartDefinition} chartRef={chartRef} />
      );
    case 'stackedArea':
      return (
        <StackedArea chartDefinition={chartDefinition} chartRef={chartRef} />
      );
    case 'horizontalBar':
      return (
        <Bar
          chartDefinition={chartDefinition}
          chartRef={chartRef}
          isHorizontal
          useSmallNoDataMessage={useSmallNoDataMessage}
        />
      );
    case 'combo':
      return (
        <Bar
          chartDefinition={chartDefinition}
          chartRef={chartRef}
          useSmallNoDataMessage={useSmallNoDataMessage}
          isCombo
        />
      );
    case 'card':
      return <Card chartDefinition={chartDefinition} />;
    case 'fixedTrendCard':
      return <FixedTrendCard chartDefinition={chartDefinition} />;
    case 'rollingTrendCard':
      return <RollingTrendCard chartDefinition={chartDefinition} />;
    case 'pie':
      return (
        <Pie
          chartDefinition={chartDefinition}
          chartRef={chartRef}
          useSmallNoDataMessage={useSmallNoDataMessage}
        />
      );
    case 'donut':
      return (
        <Donut
          chartDefinition={chartDefinition}
          chartRef={chartRef}
          useSmallNoDataMessage={useSmallNoDataMessage}
        />
      );
    case 'waterfall':
      return (
        <Waterfall
          chartDefinition={chartDefinition}
          chartRef={chartRef}
          useSmallNoDataMessage={useSmallNoDataMessage}
        />
      );
    case 'radar':
      return (
        <Radar
          chartDefinition={chartDefinition}
          chartRef={chartRef}
          useSmallNoDataMessage={useSmallNoDataMessage}
        />
      );
    case 'packedBubble':
      return (
        <PackedBubble
          chartDefinition={chartDefinition}
          chartRef={chartRef}
          useSmallNoDataMessage={useSmallNoDataMessage}
        />
      );
    case 'scatter':
      return (
        <Scatter
          chartDefinition={chartDefinition}
          chartRef={chartRef}
          useSmallNoDataMessage={useSmallNoDataMessage}
        />
      );
    case 'bubble':
      return (
        <Bubble
          chartDefinition={chartDefinition}
          chartRef={chartRef}
          useSmallNoDataMessage={useSmallNoDataMessage}
        />
      );
    case 'heatmap':
      return (
        <Heatmap
          chartDefinition={chartDefinition}
          chartRef={chartRef}
          useSmallNoDataMessage={useSmallNoDataMessage}
        />
      );
    case 'funnel':
    case 'pyramid':
      return (
        <Funnel
          chartDefinition={chartDefinition}
          chartRef={chartRef}
          useSmallNoDataMessage={useSmallNoDataMessage}
        />
      );
    case 'sankey':
      return (
        <Sankey
          chartDefinition={chartDefinition}
          chartRef={chartRef}
          useSmallNoDataMessage={useSmallNoDataMessage}
        />
      );
    case 'smartCard':
      return <SmartCard chartDefinition={chartDefinition} />;
    case 'timeSeries':
      return (
        <TimeSeries
          chartDefinition={chartDefinition}
          chartRef={chartRef}
          useSmallNoDataMessage={useSmallNoDataMessage}
        />
      );
    case 'treeMap':
      return (
        <TreeMap
          chartDefinition={chartDefinition}
          chartRef={chartRef}
          useSmallNoDataMessage={useSmallNoDataMessage}
        />
      );
    case 'simpleGrid':
      return <SimpleGrid chartDefinition={chartDefinition} />;
    case 'bullet':
      return (
        <Bullet
          chartDefinition={chartDefinition}
          useSmallNoDataMessage={useSmallNoDataMessage}
        />
      );
    default:
      return null;
  }
};

const Gate = ({
  chartDefinition,
  chartRef,
  useSmallNoDataMessage,
}: {
  chartDefinition: V5ChartDefinition;
  chartRef: React.MutableRefObject<any>;
  useSmallNoDataMessage: boolean;
}) => {
  const { currentComparison } = useContext(ComparisonContext);
  const { relativeDateRange } = useContext(DateInputContext);
  const dateScope = useDateScope({ comparison: currentComparison });
  const isSameFiscalYear = useIsSameFiscalYear();

  const isCompatible = useMemo(() => {
    if (
      currentComparison?.compareType === 'lastYear' &&
      relativeDateRange &&
      relativeDateRangeTypeCheckers.isFiscalInterval(
        relativeDateRange?.interval,
      )
    ) {
      // For fiscal intervals, we may not generate far enough back to compare against the last year.
      if (!dateScope.startDate || !dateScope.endDate) {
        return true;
      }

      const sameFiscalYear = isSameFiscalYear(
        dateScope.startDate,
        dateScope?.endDate,
        relativeDateRange.interval,
      );
      return sameFiscalYear;
    }
    return true;
  }, [
    currentComparison?.compareType,
    dateScope,
    isSameFiscalYear,
    relativeDateRange,
  ]);

  if (!isCompatible) {
    return <IncompatibleComparison />;
  }

  return (
    <V5Gadget
      chartDefinition={chartDefinition}
      chartRef={chartRef}
      useSmallNoDataMessage={useSmallNoDataMessage}
    />
  );
};

export default Gate;
